import { queryOptions } from "@tanstack/react-query";

import { api, mBrandResearchBaseURL } from "@/config/api";
import { createQueryString, createURLPath } from "@/lib/utils";
import {
  OverviewV2ErrorBodySchema,
  SnapshotResponseSchema,
  TopMoversResponseSchema,
  ExternalSignalsResponseSchema,
  SummaryResponseSchema,
  type OverviewV2SummaryResponse,
  type OverviewV2SnapshotResponse,
  type OverviewV2TopMoversResponse,
  type OverviewV2ExternalSignalsResponse,
} from "@/schema/brand-os/paid-media-overview-v2";

/**
 * Paid Media Overview v2 — API factory.
 *
 * Backend contract: GET /api/v1/ads-intelligence/paid-media-overview/{section}
 *
 * Recommended usage:
 *   - Page load → `FetchSummary` (combined `/`).
 *   - Per-section "Refresh" buttons → `FetchSnapshot|TopMovers|ExternalSignals`
 *     with `{ refresh: true }`. Backends do all caching (24h, explicit-only
 *     invalidation), so the client never needs auto-refresh polling here.
 *
 * The "what needs attention" section is gone — both insights and
 * recommendations have moved to `POST /insights/run` (see
 * `apis/brand-os/insights-run.ts`). The summary endpoint no longer
 * carries an `attention` slot.
 */

// ---------- params ----------

/**
 * Query params accepted by every endpoint. All optional — the backend has
 * env defaults for `customer_id` and `google_customer_id`. Send what we
 * know, leave the rest to the server.
 *
 * Notably: do NOT send a Meta ad-account id from the client. The backend
 * enforces `META_ADS_ACCOUNT_ID` server-side to prevent cross-tenant slips.
 */
export interface OverviewV2BaseParams {
  customer_id?: string | null;
  google_customer_id?: string | null;
  currency?: string | null;
  /** Bypasses cache. Expensive (one LLM run per platform) — use only on explicit user action. */
  refresh?: boolean | null;
  /**
   * Cap on `top_movers.movers[]` length. Backend default is 6; the
   * candidate pool from the LLM is up to 8 per platform, so 7–8 is the
   * useful upper bound. Forwarded by the combined `/` endpoint to the
   * top-movers fan-out. Omit to use the backend default.
   */
  max_movers?: number | null;
  /** Testing only. */
  today?: string | null;
}

/**
 * Extra params accepted by `/` and `/external-signals`. Insights run
 * params (user_id / prompt_id / organization_id) are NOT here — that
 * surface lives on `POST /insights/run`.
 */
export interface OverviewV2ContextParams extends OverviewV2BaseParams {
  /** ISO-2 country. Only "IN" bundled today. */
  country?: string | null;
  /** Freeform vertical hint, used only by the `web_search` provider. */
  vertical?: string | null;
  /** Override env-default provider. */
  provider?: "curated" | "web_search" | null;
}

function toQueryRecord(
  params: OverviewV2BaseParams | OverviewV2ContextParams,
): Record<string, string | number | boolean | null | undefined> {
  // Reshape `false`/`undefined` flags so `createQueryString` drops them
  // (it filters null/undefined only). For `refresh=false` we want the
  // param omitted entirely so cache keys for "fetch once" and "manual
  // refresh" don't accidentally collide.
  const out: Record<string, string | number | boolean | null | undefined> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (k === "refresh" && v === false) continue;
    out[k] = v as string | number | boolean;
  }
  return out;
}

// ---------- typed error ----------

/**
 * Thrown for any non-200 response from the v2 endpoints. Carries the
 * backend's `error_code` so the page can branch (most importantly, on
 * `NO_PLATFORM_AVAILABLE` — render a full-page "Connect an ad account"
 * state instead of the section grid).
 */
export class OverviewV2ApiError extends Error {
  readonly status: number;
  readonly errorCode: string;
  readonly details?: string;
  constructor(opts: { status: number; errorCode: string; message: string; details?: string }) {
    super(opts.message);
    this.name = "OverviewV2ApiError";
    this.status = opts.status;
    this.errorCode = opts.errorCode;
    this.details = opts.details;
  }
}

async function parseError(response: Response): Promise<OverviewV2ApiError> {
  const raw = await response.json().catch(() => ({}));
  const parsed = OverviewV2ErrorBodySchema.safeParse(raw);
  if (parsed.success) {
    return new OverviewV2ApiError({
      status: response.status,
      errorCode: parsed.data.error_code,
      message: parsed.data.error || `Request failed (${response.status})`,
      details: parsed.data.details,
    });
  }
  return new OverviewV2ApiError({
    status: response.status,
    errorCode: response.status === 503 ? "SERVICE_UNAVAILABLE" : "UNKNOWN",
    message: `Request failed (${response.status})`,
  });
}

// ---------- factory ----------

const SECTION_PATHS = {
  summary: "",
  snapshot: "snapshot",
  topMovers: "top-movers",
  externalSignals: "external-signals",
} as const;

function buildUrl(section: keyof typeof SECTION_PATHS, params: OverviewV2BaseParams) {
  const segment = SECTION_PATHS[section];
  // For the `summary` section the segment is "" — joining it via
  // `createURLPath` produces a trailing slash (`.../paid-media-overview/`)
  // which causes FastAPI's `redirect_slashes` to 307 to the canonical
  // no-slash path. That redirect (a) wastes a round-trip on every page
  // load and (b) downgrades HTTPS → HTTP in the `Location` header when
  // the upstream proxy doesn't forward `X-Forwarded-Proto`. We avoid
  // both by addressing the canonical URL directly.
  const base = segment
    ? createURLPath(PaidMediaOverviewV2ApiFactory.Endpoint, segment)
    : PaidMediaOverviewV2ApiFactory.Endpoint;
  const qs = createQueryString(toQueryRecord(params));
  return qs ? `${base}?${qs}` : base;
}

export const PaidMediaOverviewV2ApiFactory = {
  Endpoint: mBrandResearchBaseURL + "/api/v1/ads-intelligence/paid-media-overview",
  Keys: {
    // Cache-state-aware keys: any change in params (incl. `refresh` flipping
    // to `true`) forces a separate cache slot. We rely on TanStack Query to
    // dedupe identical-key concurrent requests.
    Summary: (params: OverviewV2ContextParams) =>
      ["paid-media-overview-v2", "summary", createQueryString(toQueryRecord(params))] as const,
    Snapshot: (params: OverviewV2BaseParams) =>
      ["paid-media-overview-v2", "snapshot", createQueryString(toQueryRecord(params))] as const,
    TopMovers: (params: OverviewV2BaseParams) =>
      ["paid-media-overview-v2", "top-movers", createQueryString(toQueryRecord(params))] as const,
    ExternalSignals: (params: OverviewV2ContextParams) =>
      ["paid-media-overview-v2", "external-signals", createQueryString(toQueryRecord(params))] as const,
  },
  Apis: {
    /** Combined endpoint — recommended for initial page load. */
    FetchSummary: async (params: OverviewV2ContextParams): Promise<OverviewV2SummaryResponse> => {
      const response = await api(buildUrl("summary", params));
      if (!response.ok) throw await parseError(response);
      return response.json().then(SummaryResponseSchema.parse);
    },
    FetchSnapshot: async (params: OverviewV2ContextParams): Promise<OverviewV2SnapshotResponse> => {
      const response = await api(buildUrl("snapshot", params));
      if (!response.ok) throw await parseError(response);
      return response.json().then(SnapshotResponseSchema.parse);
    },
    FetchTopMovers: async (params: OverviewV2ContextParams): Promise<OverviewV2TopMoversResponse> => {
      const response = await api(buildUrl("topMovers", params));
      if (!response.ok) throw await parseError(response);
      return response.json().then(TopMoversResponseSchema.parse);
    },
    FetchExternalSignals: async (
      params: OverviewV2ContextParams,
    ): Promise<OverviewV2ExternalSignalsResponse> => {
      const response = await api(buildUrl("externalSignals", params));
      if (!response.ok) throw await parseError(response);
      return response.json().then(ExternalSignalsResponseSchema.parse);
    },
  },
  Queries: {
    /**
     * Combined-endpoint query. `staleTime` is generous (10 min) because the
     * backend already caches for 24h — we just don't want React Query
     * thrashing the network on every focus flip while the user navigates
     * away and back to the page.
     */
    FetchSummary: (params: OverviewV2ContextParams) =>
      queryOptions({
        queryKey: PaidMediaOverviewV2ApiFactory.Keys.Summary(params),
        queryFn: () => PaidMediaOverviewV2ApiFactory.Apis.FetchSummary(params),
        staleTime: 10 * 60_000,
        gcTime: 30 * 60_000,
        // Cold path is 60–120s. Auto-retry would multiply LLM cost on the
        // backend; the page already surfaces a manual retry on failure.
        retry: false,
      }),
  },
};
